import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import { formatTimestamp, formatToDayMonthYear } from "../../utils/TextUtils.js";

function MilkSaleForm() {
    const [milkSale, setMilkSale] = useState({
        route_aggregation_id: "",
        litres: "",
        price_per_litre: "",
        total_amount: "",
        notes: ""
    });

    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isEditing, setIsEditing] = useState(false);
    const [aggregationData, setAggregationData] = useState({
        aggregation: null,
        exists: false,
        hasSale: false,
        message: ""
    });
    const [selectedAggregation, setSelectedAggregation] = useState(null);
    const [maxAvailableLitres, setMaxAvailableLitres] = useState(0);
    const [filters, setFilters] = useState({
        date: "",
        shift: "",
        route_id: ""
    });
    const [routes, setRoutes] = useState([]);
    
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("access_token");

    // Fetch routes for selection
    const fetchRoutes = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collection-routes?paginate=false`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            
            if (response.ok) {
                const data = await response.json();
                setRoutes(data.data || data || []);
            }
        } catch (error) {
            console.error("Error fetching routes:", error);
        }
    }, [token]);

    // Fetch aggregation based on date, shift, and route (for new sales only)
    const fetchAggregation = useCallback(async () => {
        if (!filters.date || !filters.shift || !filters.route_id) {
            setAggregationData({
                aggregation: null,
                exists: false,
                hasSale: false,
                message: "Please select date, shift, and route."
            });
            setSelectedAggregation(null);
            setMaxAvailableLitres(0);
            return;
        }

        try {
            const url = `${API_URL}/route-aggregations/by-date-shift?date=${filters.date}&shift=${filters.shift}&route_id=${filters.route_id}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            
            if (response.ok) {
                const data = await response.json();
                const result = data.data || {};
                
                setAggregationData({
                    aggregation: result.aggregation || null,
                    exists: result.exists || false,
                    hasSale: result.has_sale || false,
                    message: result.message || ""
                });

                // If aggregation exists and doesn't have a sale, auto-select it
                if (result.aggregation && !result.has_sale && !milkSale.route_aggregation_id) {
                    const agg = result.aggregation;
                    setSelectedAggregation(agg);
                    setMaxAvailableLitres(parseFloat(agg.litres) || 0);
                    setMilkSale(prev => ({ 
                        ...prev, 
                        route_aggregation_id: agg.id 
                    }));
                }

            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch aggregation.");
            }
        } catch (error) {
            console.error("Error fetching aggregation:", error);
            setAggregationData({
                aggregation: null,
                exists: false,
                hasSale: false,
                message: "Error fetching aggregation data."
            });
            setSelectedAggregation(null);
            setMaxAvailableLitres(0);
        }
    }, [filters, token, navigate, milkSale.route_aggregation_id]);

    const fetchMilkSale = useCallback(async () => {
        if (id) {
            try {
                const response = await fetch(`${API_URL}/milk-sales/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    const saleData = data.data || data;
                    
                    // Fetch aggregation details to get date, shift, and route
                    if (saleData.route_aggregation_id) {
                        await fetchAggregationDetails(saleData.route_aggregation_id, saleData);
                    }
                    
                    setIsEditing(true);
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: "Milk sale not found." });
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch milk sale.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching the milk sale." });
            }
        }
    }, [id, token, navigate]);

    const fetchAggregationDetails = async (aggregationId, saleData = null) => {
        try {
            const response = await fetch(`${API_URL}/route-aggregations/${aggregationId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            
            if (response.ok) {
                const data = await response.json();
                const aggregation = data.aggregation || data;
                
                // Set the aggregation data
                setSelectedAggregation(aggregation);
                setMaxAvailableLitres(parseFloat(aggregation.litres) || 0);
                
                // For editing mode, set minimal aggregation data
                if (isEditing) {
                    setAggregationData({
                        aggregation: aggregation,
                        exists: true,
                        hasSale: true,
                        message: ""
                    });
                }
                
                // Set filters based on the aggregation
                if (aggregation.collection_date && aggregation.shift && aggregation.route_id) {
                    const date = new Date(aggregation.collection_date).toISOString().split('T')[0];
                    setFilters({
                        date: date,
                        shift: aggregation.shift,
                        route_id: aggregation.route_id
                    });
                }

                // Set milk sale data
                if (saleData) {
                    setMilkSale({
                        route_aggregation_id: saleData.route_aggregation_id || aggregationId,
                        litres: saleData.litres || "",
                        price_per_litre: saleData.price_per_litre || "",
                        total_amount: saleData.total_amount || "",
                        notes: saleData.notes || ""
                    });
                } else {
                    setMilkSale(prev => ({ 
                        ...prev, 
                        route_aggregation_id: aggregationId 
                    }));
                }
            }
        } catch (error) {
            console.error("Error fetching aggregation details:", error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([
                fetchMilkSale(),
                fetchRoutes()
            ]);
            setLoading(false);
        };

        fetchData();
    }, [fetchMilkSale, fetchRoutes]);

    // Fetch aggregation when all filters are selected (for new sales only)
    useEffect(() => {
        if (filters.date && filters.shift && filters.route_id && !isEditing) {
            fetchAggregation();
        }
    }, [filters.date, filters.shift, filters.route_id, fetchAggregation, isEditing]);

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        
        // Don't allow filter changes when editing
        if (isEditing) {
            setNotification({
                type: "warning",
                message: "Cannot change route, date, or shift when editing a sale."
            });
            return;
        }
        
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        
        setMilkSale((prevMilkSale) => {
            const updatedSale = {
                ...prevMilkSale,
                [name]: value,
            };
            
            // Recalculate total amount if price or litres changes
            if ((name === 'price_per_litre' || name === 'litres') && 
                updatedSale.price_per_litre && updatedSale.litres) {
                const price = parseFloat(updatedSale.price_per_litre);
                const litres = parseFloat(updatedSale.litres);
                if (!isNaN(price) && !isNaN(litres)) {
                    updatedSale.total_amount = (price * litres).toFixed(2);
                }
            }
            
            return updatedSale;
        });
        
        // Clear error for this field
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const handleTextareaChange = (event) => {
        const { name, value } = event.target;
        setMilkSale((prevMilkSale) => ({
            ...prevMilkSale,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});
        setNotification({ type: "", message: "" });

        try {
            const method = isEditing ? "POST" : "POST";
            const url = isEditing ? 
                `${API_URL}/milk-sales/${id}/update` : 
                `${API_URL}/milk-sales`;

            // Format the data for submission
            const formData = new FormData();
            
            // Always use the selected aggregation ID
            if (selectedAggregation) {
                formData.append('route_aggregation_id', selectedAggregation.id);
            } else if (milkSale.route_aggregation_id) {
                formData.append('route_aggregation_id', milkSale.route_aggregation_id);
            } else {
                throw new Error("No aggregation selected");
            }
            
            formData.append('litres', parseFloat(milkSale.litres) || 0);
            
            if (milkSale.price_per_litre) {
                formData.append('price_per_litre', parseFloat(milkSale.price_per_litre));
            }
            
            if (milkSale.total_amount) {
                formData.append('total_amount', parseFloat(milkSale.total_amount));
            }
            
            if (milkSale.notes) {
                formData.append('notes', milkSale.notes);
            }

            const response = await fetch(url, {
                method,
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json",
                },
                body: formData,
            });

            if (response.ok) {
                setNotification({ 
                    message: isEditing ? 
                        "Milk sale updated successfully!" : 
                        "Milk sale created successfully!", 
                    type: "success" 
                });
                
                setTimeout(() => {
                    navigate(`/dashboard/milk-sales`);
                }, 1500);
                
            } else if (response.status === 401) {
                navigate("/logout");
            } else if (response.status === 422) {
                // Handle validation errors
                const errorData = await response.json();
                console.log("Validation errors:", errorData);
                
                if (errorData.errors) {
                    setErrors(errorData.errors);
                    
                    // If it's a duplicate sale error
                    if (errorData.errors.route_aggregation_id && 
                        (errorData.errors.route_aggregation_id[0].includes('already been taken') ||
                         errorData.errors.route_aggregation_id[0].includes('already exists'))) {
                        
                        // Refresh aggregation data to update status
                        if (!isEditing) {
                            await fetchAggregation();
                        }
                        
                        // Show specific notification
                        setNotification({ 
                            message: "This aggregation already has a sale. Please select different criteria.", 
                            type: "danger" 
                        });
                    }
                }
                
                // Set general error notification if not already set
                if (!notification.message) {
                    setNotification({ 
                        message: errorData.message || "Please fix the errors below.", 
                        type: "danger" 
                    });
                }
            } else {
                const errorData = await response.json();
                setNotification({ 
                    message: errorData.message || "An error occurred while saving the milk sale.", 
                    type: "danger" 
                });
            }
        } catch (error) {
            setNotification({ 
                message: "An error occurred while saving the milk sale.", 
                type: "danger" 
            });
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) return <p>Loading...</p>;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">
                            {isEditing ? "Update Milk Sale" : "Add Milk Sale"}
                        </p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/milk-sales" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back to List
                        </Link>
                    </p>
                </div>
            </nav>
            
            <div className="column dash-section">
                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <form onSubmit={handleSubmit}>
                    <div className="columns">
                        <div className="column is-half">
                            {/* Route Selection First */}
                            <div className="field">
                                <label className="label">Select Route *</label>
                                <div className="control">
                                    <div className="select is-fullwidth">
                                        <select
                                            name="route_id"
                                            value={filters.route_id}
                                            onChange={handleFilterChange}
                                            required
                                            disabled={isEditing}
                                        >
                                            <option value="">Select Route</option>
                                            {routes.map((route) => (
                                                <option key={route.id} value={route.id}>
                                                    {route.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <p className="help">Select the route first to find aggregations</p>
                            </div>

                            {/* Date and Shift Selection (only enabled after route is selected) */}
                            <div className="field">
                                <label className="label">Select Date and Shift *</label>
                                <div className="columns">
                                    <div className="column">
                                        <div className="control">
                                            <input
                                                className="input"
                                                type="date"
                                                name="date"
                                                value={filters.date}
                                                onChange={handleFilterChange}
                                                max={new Date().toISOString().split('T')[0]}
                                                required
                                                disabled={isEditing || !filters.route_id}
                                            />
                                        </div>
                                    </div>
                                    <div className="column">
                                        <div className="control">
                                            <div className="select is-fullwidth">
                                                <select
                                                    name="shift"
                                                    value={filters.shift}
                                                    onChange={handleFilterChange}
                                                    required
                                                    disabled={isEditing || !filters.route_id}
                                                >
                                                    <option value="">Select Shift</option>
                                                    <option value="Morning">Morning</option>
                                                    <option value="Evening">Evening</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p className="help">
                                    {!filters.route_id 
                                        ? "Please select a route first"
                                        : "Select the date and shift to find aggregation"}
                                </p>
                            </div>

                            {!isEditing && filters.route_id && filters.date && filters.shift && (
                                <div className="field">
                                    <div className="box">
                                        {!aggregationData.exists ? (
                                            <div className="message is-danger">
                                                <div className="message-body">
                                                    <p><strong>No Aggregation Found</strong></p>
                                                    <p>No route aggregation exists for:</p>
                                                    <ul>
                                                        <li><strong>Route:</strong> {routes.find(r => r.id == filters.route_id)?.name}</li>
                                                        <li><strong>Date:</strong> {filters.date}</li>
                                                        <li><strong>Shift:</strong> {filters.shift}</li>
                                                    </ul>
                                                    <p className="mt-2">
                                                        Please create the aggregation first or select different criteria.
                                                    </p>
                                                </div>
                                            </div>
                                        ) : aggregationData.hasSale ? (
                                            <div className="message is-warning">
                                                <div className="message-body">
                                                    <p><strong>Sale Already Exists</strong></p>
                                                    <p>An aggregation exists but already has a sale record.</p>
                                                    <ul>
                                                        <li><strong>Route:</strong> {routes.find(r => r.id == filters.route_id)?.name}</li>
                                                        <li><strong>Date:</strong> {filters.date}</li>
                                                        <li><strong>Shift:</strong> {filters.shift}</li>
                                                    </ul>
                                                    <p className="mt-2">
                                                        You cannot create another sale for the same aggregation.
                                                    </p>
                                                   
                                                </div>
                                            </div>
                                        ) : selectedAggregation ? (
                                            <></>
                                        ) : (
                                            <div className="message is-info">
                                                <div className="message-body">
                                                    <p><strong>Loading Aggregation...</strong></p>
                                                    <p>Please wait while we fetch the aggregation details.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Sale Details Form - Show for BOTH editing AND when aggregation is available for sale */}
                            {(isEditing && milkSale.route_aggregation_id) || (selectedAggregation && !aggregationData.hasSale) ? (
                                <>
                                    {/* Litres to Sell */}
                                    <div className="field">
                                        <label className="label">
                                            Litres to Sell * (Max: {maxAvailableLitres.toFixed(2)} L)
                                        </label>
                                        <div className="control">
                                            <input
                                                className={`input ${errors.litres ? "is-danger" : ""}`}
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                max={maxAvailableLitres}
                                                name="litres"
                                                value={milkSale.litres}
                                                onChange={handleChange}
                                                placeholder="Enter litres to sell"
                                                required
                                            />
                                        </div>
                                        {errors.litres && errors.litres.map((error, index) => (
                                            <p key={index} className="help is-danger">{error}</p>
                                        ))}
                                        {maxAvailableLitres > 0 && milkSale.litres && (
                                            <p className="help">
                                                Remaining for processor: {(maxAvailableLitres - parseFloat(milkSale.litres || 0)).toFixed(2)} L
                                            </p>
                                        )}
                                    </div>

                                    {/* Price per Litre */}
                                    <div className="field">
                                        <label className="label">Price per Litre (Optional)</label>
                                        <div className="control">
                                            <input
                                                className={`input ${errors.price_per_litre ? "is-danger" : ""}`}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="price_per_litre"
                                                value={milkSale.price_per_litre}
                                                onChange={handleChange}
                                                placeholder="Enter price per litre"
                                            />
                                        </div>
                                        {errors.price_per_litre && errors.price_per_litre.map((error, index) => (
                                            <p key={index} className="help is-danger">{error}</p>
                                        ))}
                                    </div>

                                    {/* Total Amount */}
                                    <div className="field">
                                        <label className="label">Total Amount (Auto-calculated)</label>
                                        <div className="control">
                                            <input
                                                className="input"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="total_amount"
                                                value={milkSale.total_amount}
                                                readOnly
                                                placeholder="Will be calculated from price × litres"
                                            />
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div className="field">
                                        <label className="label">Notes</label>
                                        <div className="control">
                                            <textarea
                                                className={`textarea ${errors.notes ? "is-danger" : ""}`}
                                                name="notes"
                                                value={milkSale.notes}
                                                onChange={handleTextareaChange}
                                                placeholder="Additional notes about this sale..."
                                                rows="3"
                                            />
                                        </div>
                                        {errors.notes && errors.notes.map((error, index) => (
                                            <p key={index} className="help is-danger">{error}</p>
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </div>

                        {/* Right column for aggregation info */}
                        <div className="column is-half">
                            {/* Show aggregation details when editing OR when we have a selected aggregation */}
                            {(isEditing && milkSale.route_aggregation_id) || selectedAggregation ? (
                                <>
                                    <article className="message is-info">
                                        <div className="message-header">
                                            <p><i className="fas fa-info-circle"></i> &nbsp; Aggregation Details</p>
                                        </div>
                                        <div className="message-body">
                                            <div className="content">
                                                {selectedAggregation ? (
                                                    <>
                                                        <p><strong>Route:</strong> {selectedAggregation.route?.name}</p>
                                                        <p><strong>Date:</strong> {formatToDayMonthYear(selectedAggregation.collection_date)}</p>
                                                        <p><strong>Shift:</strong> {selectedAggregation.shift}</p>
                                                        <p><strong>Aggregated Litres:</strong> {parseFloat(selectedAggregation.litres).toFixed(2)} L</p>
                                                        <p><strong>Aggregator:</strong> {selectedAggregation.aggregator?.name}</p>
                                                    </>
                                                ) : isEditing ? (
                                                    <>
                                                        <p><strong>Route:</strong> {routes.find(r => r.id == filters.route_id)?.name}</p>
                                                        <p><strong>Date:</strong> {filters.date}</p>
                                                        <p><strong>Shift:</strong> {filters.shift}</p>
                                                        <p><strong>Status:</strong> Editing existing sale</p>
                                                    </>
                                                ) : null}
                                                
                                                <hr />
                                                
                                                <div className="box">
                                                    <p className="has-text-weight-bold">Sale Summary:</p>
                                                    <ul>
                                                        <li>Available to sell: <strong>{maxAvailableLitres.toFixed(2)} L</strong></li>
                                                        <li>Selected to sell: <strong>{milkSale.litres ? parseFloat(milkSale.litres).toFixed(2) : '0.00'} L</strong></li>
                                                        <li>Remaining for processor: <strong>{(maxAvailableLitres - parseFloat(milkSale.litres || 0)).toFixed(2)} L</strong></li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                </>
                            ) : filters.route_id && filters.date && filters.shift ? (
                                <article className="message is-warning">
                                  
                                    <div className="message-body">
                                        {!aggregationData.exists ? (
                                            <div>
                                                <p><strong>No Aggregation Found</strong></p>
                                                <p>For the selected criteria:</p>
                                                <ul>
                                                    <li><strong>Route:</strong> {routes.find(r => r.id == filters.route_id)?.name}</li>
                                                    <li><strong>Date:</strong> {filters.date}</li>
                                                    <li><strong>Shift:</strong> {filters.shift}</li>
                                                </ul>
                                                
                                            </div>
                                        ) : (
                                            <div>
                                                <p><strong>Sale Already Exists</strong></p>
                                                <p>An aggregation exists but cannot be used for a new sale.</p>
                                                <p>Please select different criteria or view the existing sale.</p>
                                            </div>
                                        )}
                                    </div>
                                </article>
                            ) : filters.route_id ? (
                                <article className="message is-warning">
                                    <div className="message-header">
                                        <p><i className="fas fa-exclamation-triangle"></i> &nbsp; Select Date and Shift</p>
                                    </div>
                                    <div className="message-body">
                                        <p>Route selected: <strong>{routes.find(r => r.id == filters.route_id)?.name}</strong></p>
                                        <p>Please select a date and shift to find aggregations.</p>
                                        <p>Aggregations are created per route, date, and shift combination.</p>
                                    </div>
                                </article>
                            ) : (
                                <article className="message is-warning">
                                    <div className="message-header">
                                        <p><i className="fas fa-exclamation-triangle"></i> &nbsp; Select Route First</p>
                                    </div>
                                    <div className="message-body">
                                        <p>Please select a route first to find aggregations.</p>
                                    </div>
                                </article>
                            )}

                            <article className="message is-light">
                                <div className="message-header">
                                    <p><i className="fas fa-question-circle"></i> &nbsp; How Milk Sales Work</p>
                                </div>
                                <div className="message-body">
                                    <ol>
                                        <li><strong>Select Route</strong> - Choose the collection route</li>
                                        <li><strong>Select Date & Shift</strong> - Choose the specific aggregation</li>
                                        <li><strong>Check Aggregation</strong> - System finds if aggregation exists</li>
                                        <li><strong>Enter Sale Details</strong> - If aggregation exists and has no sale</li>
                                        <li><strong>Record Sale</strong> - Save the milk sale record</li>
                                    </ol>
                                    <p className="has-text-weight-bold mt-2">Important:</p>
                                    <ul>
                                        <li>Each aggregation can only have one sale record</li>
                                        <li>Cannot sell more milk than was aggregated</li>
                                        <li>Aggregation must exist before recording sale</li>
                                    </ul>
                                </div>
                            </article>
                        </div>
                    </div>

                    {/* Submit Button */}
                    {(isEditing && milkSale.route_aggregation_id) || (selectedAggregation && !aggregationData.hasSale) ? (
                        <div className="columns">
                            <div className="column is-half">
                                <div className="field">
                                    <div className="control">
                                        <button 
                                            type="submit" 
                                            className="button primary-bg is-pulled-right is-fullwidth" 
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="icon">
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                    </span>
                                                    <span>&nbsp; Processing...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="icon">
                                                        <i className={isEditing ? "fas fa-save" : "fas fa-check"}></i>
                                                    </span>
                                                    <span>&nbsp; {isEditing ? "UPDATE SALE" : "RECORD SALE"}</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </form>
            </div>
        </main>
    );
}

export default MilkSaleForm;